import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TablePagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Radio, RadioGroup, FormControlLabel, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/Delete';

import { sftpAccessAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const PER = 25;

function AddDialog({ customer, region, groups, suggested, onClose, onAdded }) {
  const [groupId, setGroupId] = useState(suggested || '');
  const [form, setForm] = useState({ ip: '', ticket: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ready = groupId && form.ip.trim() && form.ticket.trim() && form.reason.trim();

  const save = async () => {
    setBusy(true); setError(null);
    try { const res = await sftpAccessAPI.addIp({ customer_id: customer.id, group_id: groupId, ...form }); onAdded(res.data); }
    catch (err) { setError(errText(err, 'Could not add the address')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Add an IP address for {customer.cust_code}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The address is allowed to connect to the SFTP server in {region} on port 22, straight away. Only a single address can be added; it is always saved as /32.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Security group</Typography>
        <RadioGroup value={groupId} onChange={(e) => setGroupId(e.target.value)} sx={{ mb: 2 }}>
          {groups.map((g) => (
            <FormControlLabel key={g.group_id} value={g.group_id} disabled={g.free === 0} control={<Radio size="small" />}
              label={`${g.name} - ${g.free === 0 ? 'full' : `${g.free} free of ${g.limit}`}${g.customer_rules ? ` - already holds ${g.customer_rules} of ${customer.cust_code}'s addresses` : ''}${g.group_id === suggested ? ' (suggested)' : ''}`} />
          ))}
        </RadioGroup>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="IP address" value={form.ip} onChange={set('ip')} required autoFocus placeholder="203.0.113.5" helperText="IPv4, for example 52.20.30.40. Private addresses are refused." />
          <TextField label="Ticket number" value={form.ticket} onChange={set('ticket')} required slotProps={{ htmlInput: { maxLength: 30 } }}
            helperText="The ticket that asked for this. It is saved with the change." />
          <TextField label="Reason" value={form.reason} onChange={set('reason')} required multiline minRows={2} slotProps={{ htmlInput: { maxLength: 500 } }}
            helperText="For example what the address is for. It goes in the rule description with the customer code and ticket." />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !ready}>{busy ? 'Adding...' : 'Add address'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveDialog({ customer, group, rule, onClose, onRemoved }) {
  const [form, setForm] = useState({ ticket: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ready = form.ticket.trim() && form.reason.trim();

  const save = async () => {
    setBusy(true); setError(null);
    try { const res = await sftpAccessAPI.removeIp({ customer_id: customer.id, group_id: group.group_id, rule_id: rule.rule_id, ...form }); onRemoved(res.data); }
    catch (err) { setError(errText(err, 'Could not remove the address')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Remove {rule.address}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {rule.address} ({rule.description || 'no description'}) is taken out of {group.name} straight away and can no longer connect to SFTP.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Ticket number" value={form.ticket} onChange={set('ticket')} required autoFocus slotProps={{ htmlInput: { maxLength: 30 } }}
            helperText="The ticket that asked for this. It is saved with the change." />
          <TextField label="Reason" value={form.reason} onChange={set('reason')} required multiline minRows={2} slotProps={{ htmlInput: { maxLength: 500 } }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="error" onClick={save} disabled={busy || !ready}>{busy ? 'Removing...' : 'Remove address'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function SftpAccess() {
  const { isAdmin } = usePermissions();
  const [removing, setRemoving] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [info, setInfo] = useState(null);
  const [viewGroup, setViewGroup] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [audit, setAudit] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [adding, setAdding] = useState(false);

  const loadAudit = useCallback(() => { sftpAccessAPI.audit().then((r) => setAudit(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    sftpAccessAPI.customers().then((r) => setCustomers(r.data)).catch((err) => { setCustomers([]); setError(errText(err, 'Could not load the customers')); });
    loadAudit();
  }, [loadAudit]);

  const loadGroups = useCallback(async (id, keepGroup) => {
    if (!id) { setInfo(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await sftpAccessAPI.groups(id);
      setInfo(res.data);
      setViewGroup((cur) => (keepGroup && res.data.groups.some((g) => g.group_id === cur) ? cur : res.data.suggested || res.data.groups[0]?.group_id || ''));
    } catch (err) { setInfo(null); setError(errText(err, 'Could not read the security groups')); }
    setLoading(false);
  }, []);

  const pick = (e) => { setCustomerId(e.target.value); setNotice(null); setSearch(''); setPage(0); loadGroups(e.target.value, false); };
  const group = info?.groups.find((g) => g.group_id === viewGroup);
  const rules = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (group?.rules || []).filter((r) => !q || r.address.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));
  }, [group, search]);
  useEffect(() => { setPage(0); }, [search, viewGroup]);
  const mine = (r) => info && new RegExp(`(^|[^A-Za-z0-9])${info.customer.cust_code}([^A-Za-z0-9]|$)`, 'i').test(r.description || '');

  return (
    <Box>
      <Typography variant="h4" gutterBottom>SFTP Access</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Allow a customer's IP address to connect to the SFTP server in the region where the customer is hosted. Choose the customer to see the security groups for its region.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField select label="Customer" value={customerId} onChange={pick} slotProps={NATIVE} sx={{ minWidth: 320 }}>
            <option value="">{customers === null ? 'Loading...' : 'Select a customer'}</option>
            {(customers || []).map((c) => <option key={c.id} value={c.id}>{c.cust_code} - {c.cust_name}{c.supported ? ` (${c.location})` : ' (no SFTP group)'}</option>)}
          </TextField>
          {loading && <CircularProgress size={22} />}
          {info && <Chip label={`${info.customer.location} - ${info.region}`} />}
          {info && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)} disabled={!info.suggested}>Add IP address</Button>}
        </Box>
        {info && !info.suggested && <Alert severity="warning" sx={{ mt: 2 }}>Every SFTP group in this region is full. Ask the Cloud team to add a group.</Alert>}
      </Paper>

      {info && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField select size="small" label="Security group" value={viewGroup} onChange={(e) => setViewGroup(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 420 }}>
              {info.groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name} - {g.used} of {g.limit} rules used{g.customer_rules ? ` (${g.customer_rules} for ${info.customer.cust_code})` : ''}</option>)}
            </TextField>
            <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Address</TableCell><TableCell>Description</TableCell>{isAdmin() && <TableCell />}</TableRow></TableHead>
              <TableBody>
                {rules.slice(page * PER, page * PER + PER).map((r) => (
                  <TableRow key={r.rule_id} hover selected={mine(r)}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.address}</TableCell>
                    <TableCell>{r.description || '-'}</TableCell>
                    {isAdmin() && <TableCell align="right"><Tooltip title="Remove this address"><IconButton size="small" aria-label={`Remove ${r.address}`} onClick={() => setRemoving(r)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip></TableCell>}
                  </TableRow>
                ))}
                {rules.length === 0 && <TableRow><TableCell colSpan={3}><Typography color="text.secondary">No addresses match.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={rules.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={PER} rowsPerPageOptions={[PER]} />
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Recent changes</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>Change</TableCell><TableCell>Customer</TableCell><TableCell>Address</TableCell><TableCell>Group</TableCell><TableCell>Ticket</TableCell><TableCell>Reason</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
            <TableBody>
              {audit.slice(0, 20).map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{fmtTime(a.requested_at)}</TableCell>
                  <TableCell>{a.requested_name || a.requested_by}</TableCell>
                  <TableCell>{a.action === 'remove' ? 'Removed' : 'Added'}</TableCell>
                  <TableCell>{a.customer_code}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{a.ip_address}</TableCell>
                  <TableCell>{a.group_name} ({a.region})</TableCell>
                  <TableCell>{a.ticket}</TableCell>
                  <TableCell>{a.reason}</TableCell>
                  <TableCell><Chip size="small" label={a.result} color={a.result === 'success' ? 'success' : a.result === 'failed' ? 'error' : 'default'} title={a.error || ''} /></TableCell>
                </TableRow>
              ))}
              {audit.length === 0 && <TableRow><TableCell colSpan={9}><Typography color="text.secondary">No changes made through the portal yet.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {removing && info && group && <RemoveDialog customer={info.customer} group={group} rule={removing} onClose={() => setRemoving(null)}
        onRemoved={(r) => { setRemoving(null); setNotice(`${r.removed} has been removed from ${r.group}.`); loadGroups(customerId, true); loadAudit(); }} />}
      {adding && info && <AddDialog customer={info.customer} region={info.region} groups={info.groups} suggested={info.suggested} onClose={() => setAdding(false)}
        onAdded={(r) => { setAdding(false); setNotice(`${r.added} is now allowed to connect to SFTP in ${r.region} (${r.group}).`); loadGroups(customerId, true); loadAudit(); }} />}
    </Box>
  );
}

export default SftpAccess;
